import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, MapPin, Star, X, ChevronDown, Sparkles } from 'lucide-react';
import axios from 'axios';
import { businessApi, categoriesApi } from '../services/api';

const isNewListing = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return created > sevenDaysAgo;
};

const BusinessCard = ({ business, reviewStats }) => {
  const isNew = isNewListing(business.created_at);
  const stats = reviewStats?.[business.id];

  return (
    <Link
      to={`/businesses/${business.slug || business.id}`}
      className={`business-card hover-lift ${business.is_featured ? 'featured' : ''}`}
      data-testid={`business-card-${business.id}`}
    >
      <div className="business-card-image flex items-center justify-center text-neutral-900 text-4xl font-bold">
        {business.photos?.[0] ? (
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[0]}`} 
            alt={business.business_name} 
            className="w-full h-full object-contain" 
          />
        ) : (
          business.business_name.charAt(0)
        )}
      </div>
      <div className="business-card-content">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-2 min-w-0">
            <h3 className="font-bold text-lg text-neutral-900 line-clamp-1">{business.business_name}</h3>
            {isNew && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 text-green-600 rounded-full text-xs font-semibold flex-shrink-0" data-testid="new-badge">
                <Sparkles className="w-3 h-3" /> New
              </span>
            )}
          </div>
          {business.is_featured && (
            <span className="badge badge-featured flex items-center gap-1 flex-shrink-0">
              <Star className="w-3 h-3" /> Featured
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-500 mb-2">
          {business.primary_category}
          {business.subcategory && <span className="text-neutral-400"> &middot; {business.subcategory}</span>}
        </p>
        {stats && stats.count > 0 && (
          <div className="flex items-center gap-1 mb-2" data-testid={`card-rating-${business.id}`}>
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-semibold text-neutral-800">{stats.average_rating.toFixed(1)}</span>
            <span className="text-xs text-neutral-500">({stats.count})</span>
          </div>
        )}
        <p className="text-sm text-neutral-600 line-clamp-2 mb-3">{business.description}</p>
        <div className="flex items-center gap-1 text-sm text-neutral-500 mb-3">
          <MapPin className="w-4 h-4" />
          <span className="line-clamp-1">{business.city_state_zip}</span>
        </div>
        {business.ownership_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {business.ownership_tags.slice(0, 2).map(tag => (
              <span key={tag} className="badge badge-minority text-xs">{tag}</span>
            ))}
            {business.ownership_tags.length > 2 && (
              <span className="badge badge-category text-xs">+{business.ownership_tags.length - 2}</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

const BusinessDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [ownershipTags, setOwnershipTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '');
  const [selectedOwnership, setSelectedOwnership] = useState(searchParams.get('ownership') || '');

  useEffect(() => {
    loadCategories();
    loadReviewStats();
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [selectedCategory, selectedSubcategory, selectedOwnership]);

  useEffect(() => {
    const loadSubs = async () => {
      if (selectedCategory) {
        try {
          const res = await categoriesApi.getBusinessSubcategories(selectedCategory);
          setSubcategories(res.data);
        } catch { setSubcategories([]); }
      } else {
        setSubcategories([]);
        setSelectedSubcategory('');
      }
    };
    loadSubs();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const [catRes, tagRes] = await Promise.all([
        categoriesApi.getBusinessCategories(),
        categoriesApi.getOwnershipTags()
      ]);
      setCategories(catRes.data);
      setOwnershipTags(tagRes.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadReviewStats = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/reviews/stats`);
      setReviewStats(res.data || {});
    } catch (e) {
      // non-fatal
    }
  };

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSubcategory) params.subcategory = selectedSubcategory;
      if (selectedOwnership) params.ownership = selectedOwnership;
      if (search) params.search = search;

      const response = await businessApi.getAll(params);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadBusinesses();
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedOwnership('');
    setSearchParams({});
  };

  const hasActiveFilters = search || selectedCategory || selectedSubcategory || selectedOwnership;

  // Group businesses by category when "All Categories" is selected
  const renderBusinessGrid = () => {
    if (selectedCategory || search) {
      // Specific category or search: flat list, featured first
      const sorted = [...businesses].sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return 0;
      });
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sorted.map(b => <BusinessCard key={b.id} business={b} reviewStats={reviewStats} />)}
        </div>
      );
    }

    // All Categories: group by category, featured first within each
    const grouped = {};
    const featuredAll = [];

    businesses.forEach(b => {
      if (b.is_featured) {
        featuredAll.push(b);
      }
      const cat = b.primary_category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(b);
    });

    // Sort categories alphabetically
    const sortedCats = Object.keys(grouped).sort();

    return (
      <div className="space-y-12">
        {/* Featured section at top */}
        {featuredAll.length > 0 && (
          <div data-testid="featured-section">
            <div className="flex items-center gap-2 mb-5">
              <Star className="w-5 h-5 text-accent fill-accent" />
              <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Featured Businesses
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {featuredAll.map(b => <BusinessCard key={`feat-${b.id}`} business={b} reviewStats={reviewStats} />)}
            </div>
          </div>
        )}

        {/* Category groups */}
        {sortedCats.map(cat => {
          const catBusinesses = grouped[cat]
            .filter(b => !b.is_featured)
            .sort((a, b) => {
              // New listings first within each category
              const aNew = isNewListing(a.created_at) ? 1 : 0;
              const bNew = isNewListing(b.created_at) ? 1 : 0;
              return bNew - aNew;
            });

          if (catBusinesses.length === 0) return null;

          return (
            <div key={cat} data-testid={`category-group-${cat}`}>
              <div className="flex items-center gap-3 mb-5">
                <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {cat}
                </h2>
                <span className="text-sm text-neutral-500 bg-neutral-100 px-2.5 py-0.5 rounded-full">
                  {catBusinesses.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {catBusinesses.map(b => <BusinessCard key={b.id} business={b} reviewStats={reviewStats} />)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="business-directory">
      <Helmet>
        <title>Business Directory | Local Businesses in Martinsville & Henry County, VA | Explore MHC</title>
        <meta name="description" content="Browse and search local businesses in Martinsville and Henry County, Virginia. Find restaurants, shops, contractors, healthcare, and services near you." />
        <link rel="canonical" href="https://exploremhc.com/businesses" />
        <meta property="og:title" content="Business Directory | Local Businesses in Martinsville & Henry County, VA" />
        <meta property="og:description" content="Browse and search local businesses in Martinsville and Henry County, Virginia. Find restaurants, shops, contractors, healthcare, and services near you." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1773020678367-8f11b8761240?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=630&fit=crop" />
      </Helmet>
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12">
          <h1 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-2 md:mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Business Directory
          </h1>
          <p className="text-base md:text-lg text-neutral-600">
            Explore local businesses in Martinsville and Henry County
          </p>
        </div>
      </div>

      {/* Sticky Search Bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-neutral-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <form onSubmit={handleSearch} className="flex gap-2 md:gap-4 max-w-2xl">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search businesses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-12 pl-12 pr-4 rounded-xl border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                data-testid="directory-search-input"
              />
            </div>
            <button type="submit" className="btn-primary" data-testid="directory-search-button">
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden btn-secondary flex items-center gap-2"
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </form>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className={`w-full lg:w-72 lg:flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="filter-sidebar">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">Filters</h3>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-primary hover:underline" data-testid="clear-filters">
                    Clear all
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="form-label">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }}
                  className="form-input"
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory Filter */}
              {subcategories.length > 0 && (
                <div className="mb-6">
                  <label className="form-label">Subcategory</label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                    className="form-input"
                    data-testid="subcategory-filter"
                  >
                    <option value="">All Subcategories</option>
                    {subcategories.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Ownership Filter */}
              <div className="mb-6">
                <label className="form-label">Ownership</label>
                <select
                  value={selectedOwnership}
                  onChange={(e) => setSelectedOwnership(e.target.value)}
                  className="form-input"
                  data-testid="ownership-filter"
                >
                  <option value="">All Businesses</option>
                  <option value="Minority-Owned">All Minority-Owned</option>
                  {ownershipTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="pt-4 border-t border-neutral-200">
                  <p className="text-sm font-medium text-neutral-600 mb-3">Active Filters</p>
                  <div className="flex flex-wrap gap-2">
                    {search && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        "{search}"
                        <button onClick={() => setSearch('')}><X className="w-4 h-4" /></button>
                      </span>
                    )}
                    {selectedCategory && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {selectedCategory}
                        <button onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); }}><X className="w-4 h-4" /></button>
                      </span>
                    )}
                    {selectedSubcategory && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {selectedSubcategory}
                        <button onClick={() => setSelectedSubcategory('')}><X className="w-4 h-4" /></button>
                      </span>
                    )}
                    {selectedOwnership && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {selectedOwnership}
                        <button onClick={() => setSelectedOwnership('')}><X className="w-4 h-4" /></button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Business Grid */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <p className="text-neutral-600">
                {loading ? 'Loading...' : `${businesses.length} businesses found`}
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : businesses.length > 0 ? (
              renderBusinessGrid()
            ) : (
              <div className="empty-state">
                <Search className="empty-state-icon" />
                <h3 className="text-xl font-bold text-neutral-900 mb-2">No businesses found</h3>
                <p className="mb-4">Try adjusting your search or filters</p>
                <button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default BusinessDirectory;
